import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createPortal } from 'react-dom'
import { monadClient } from '../api/client'
import { refreshAfterSetChange } from '../api/afterWrite'
import { attributesQuery, typeQuery } from '../api/typeMetadata'
import { resolveWidget } from '../widgets/registry'
import { Checkbox } from '../widgets/Checkbox'
import { resolvePrimaryAttributeId } from '../api/primaryAttribute'
import { Button } from '../widgets/Button'
import { normalizeSubStructure, type SubStructure } from '../picker/useAttributeLevel'
import { validateComposite } from '../widgets/subFieldKinds'
import type { PlaceDetails } from '../api/client'
import { PlacePickContext, placeFill } from '../places/places'

/**
 * Module-level stack of currently-mounted modal depths. Each CreateRecordModal
 * pushes its depth on mount and pops it on unmount. The keydown handler acts
 * only when its own depth equals max(stack) — i.e. it is the topmost modal.
 * This prevents ⌘↵ / Esc from firing on every level in a nested stack.
 */
const _modalDepthStack: number[] = []

/** Attribute metadata a create field needs (same shape the old inline form used). */
interface FormAttr {
  attribute_id: string; name: string; type: string; slug?: string
  is_system?: boolean; is_required?: boolean; config?: Record<string, unknown> | null
  /** Composite sub-fields with their served constraint kinds (SEN-1780) —
   *  the vocabulary that lets the form refuse a value the codec would. */
  sub_structure?: SubStructure
  /** Declared roles (`display.semantic`) — a picked place fills the
   *  attribute declared to hold its place id. */
  display?: { semantic?: unknown } | null
  /** SEN-437: the served default (applied by the server on create): `value`
   *  pre-fills the field; `system` names a value the server fills itself. */
  default_value?: { value?: unknown; system?: unknown } | null
}

/** The fields' served defaults that pre-fill the form (a value the person can change or clear). */
export function prefilledDefaults(fields: readonly FormAttr[]): Record<string, unknown> {
  return Object.fromEntries(fields
    .filter((f) => f.default_value && f.default_value.value !== undefined && f.default_value.value !== null)
    .map((f) => [f.attribute_id, f.default_value!.value]))
}

/** What a server-filled default is, in words (`{system: 'created_at'}`). */
const SYSTEM_DEFAULT_TEXT: Record<string, string> = { created_at: 'Set to the creation time' }

/**
 * The MINIMAL EDITOR (Attio "Create 〈Type〉" modal). Registry-driven: a field
 * appears iff its kind registers a `write` editor — no hardcoded kind list.
 * Commit = POST /records + per-cell setValue (the loader's path). Reference
 * fields dispatch the reference editor, whose "+ Create new" opens a NESTED
 * CreateRecordModal — recursion comes from the registry, this component only
 * renders one stack level and is mounted again by the level above it.
 */
export function CreateRecordModal({ slug, typeId, onCreated, onClose, depth = 0, prefill, container }: {
  slug: string; typeId: string
  /** Values the host starts the form with, by attribute SLUG (a record created from a site's page: the
   *  site). Ordinary drafts: the person can change or clear them; they are written on Create. */
  prefill?: Record<string, unknown>
  /** Where the modal is drawn: the page (default), or the element of a host that traps focus and pointer events (a dialog:
   *  anything outside it could not be typed into). */
  container?: HTMLElement | null
  onCreated: (recordId: string) => void
  onClose: () => void
  depth?: number
}) {
  const queryClient = useQueryClient()
  // SEN-180: the ONE type-metadata seam (api/typeMetadata).
  const { data: type } = useQuery(typeQuery(slug, typeId))
  // PR280-A8C3 finding 2: the query's STATE is load-bearing, not just its
  // data. `is_required` is served metadata, so until it arrives the modal
  // cannot know what "valid" means — an empty `attrs` and a type with no
  // requirements are indistinguishable from the data alone. The modal
  // therefore tracks resolution explicitly and refuses to submit without it.
  const { data: attrs, isPending: attrsPending, isError: attrsError, error: attrsErrorObj, refetch: refetchAttrs } =
    useQuery(attributesQuery(slug, typeId))
  const metadataReady = !attrsPending && !attrsError && attrs !== undefined
  const allFields = ((attrs ?? []) as unknown as FormAttr[]).filter(
    (a) => !a.is_system && (a.config as { is_reverse?: boolean } | null)?.is_reverse !== true && Boolean(resolveWidget(a.type)?.write),
  )
  // Primary attribute first (name-named → first text → first), then the type's
  // curated fields in their order (display.highlights, the record page's own —
  // SEN-437: a meter's site and energy source, not the alphabet), rest follow.
  const primaryId = resolvePrimaryAttributeId(allFields)
  const highlights = ((type as { display?: { highlights?: unknown } } | undefined)?.display?.highlights)
  const rank = new Map((Array.isArray(highlights) ? highlights : []).map((id, i) => [String(id), i]))
  const ordered = rank.size === 0 ? allFields : [...allFields].sort(
    (a, b) => (rank.get(a.attribute_id) ?? rank.size) - (rank.get(b.attribute_id) ?? rank.size))
  const fields = primaryId
    ? [
        ...ordered.filter((f) => f.attribute_id === primaryId),
        ...ordered.filter((f) => f.attribute_id !== primaryId),
      ]
    : ordered
  const [values, setValues] = useState<Record<string, unknown>>({})
  // SEN-437: the type's defaults pre-fill the form once its metadata arrives (and
  // after a Create-more reset): the person sees what the server would apply.
  const defaults = prefilledDefaults(fields)
  const prefillById = Object.fromEntries(fields
    .filter((f) => f.slug !== undefined && prefill && f.slug in prefill && prefill[f.slug] != null)
    .map((f) => [f.attribute_id, prefill![f.slug!]]))
  const seedKey = JSON.stringify({ ...defaults, ...prefillById })
  const [createMore, setCreateMore] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Incremented on "Create more" to remount all field editors and clear their internal state.
  const [fieldsKey, setFieldsKey] = useState(0)
  // Per field: bumped when a picked place fills it, so that one editor
  // remounts and shows the value (editors hold their own draft).
  const [filledRev, setFilledRev] = useState<Record<string, number>>({})

  /** A place picked in any location editor fills every EMPTY field whose
   *  kind declares what a place gives it (`WidgetSpec.fromPlace`): the hours,
   *  the name on the primary attribute, the place id on the attribute whose
   *  role says it holds one. Filled values are ordinary drafts — the person
   *  can change any of them before Create. */
  function fillFromPlace(place: PlaceDetails, sourceAttributeId: string | undefined) {
    const patch = placeFill(fields, values, place, {
      primaryId,
      sourceAttributeId,
      fromPlace: (kind) => resolveWidget(kind)?.fromPlace,
    })
    const ids = Object.keys(patch)
    if (ids.length === 0) return
    setValues((p) => ({ ...p, ...patch }))
    setFilledRev((r) => ({ ...r, ...Object.fromEntries(ids.map((id) => [id, (r[id] ?? 0) + 1])) }))
  }

  /** SEN-1781 P1-3: REQUIRED fields are enforced from SERVED metadata
   *  (`attribute.is_required`) before anything is written. The form rendered
   *  the required marker but never checked it, so an empty required field
   *  created a record anyway and the asterisk was decoration. Nothing here
   *  hardcodes a slug: whichever attributes the registry serves as required
   *  are the ones enforced. Whitespace does not satisfy a text requirement. */
  function missingRequired(): string[] {
    return fields
      .filter((f) => f.is_required)
      .filter((f) => {
        const v = values[f.attribute_id]
        if (v === undefined || v === null || v === '') return true
        return typeof v === 'string' && v.trim() === ''
      })
      .map((f) => f.name)
  }

  /** SEN-1780: composite sub-fields are checked against their SERVED
   *  constraint kinds before anything is created. The composite editors
   *  already refuse locally, but a value can also arrive from a draft that
   *  predates a metadata change — and a refusal after `createRecord` is the
   *  expensive kind: it strands a half-written record. */
  function invalidComposites(): string[] {
    const out: string[] = []
    for (const f of fields) {
      const subs = normalizeSubStructure(f.sub_structure).filter((s) => !s.read_only)
      if (subs.length === 0) continue
      const v = values[f.attribute_id]
      if (v === undefined || v === null || typeof v !== 'object') continue
      const draft = Object.fromEntries(
        Object.entries(v as Record<string, unknown>).map(([k, val]) => [k, val == null ? '' : String(val)]),
      )
      for (const problem of validateComposite(subs, draft)) out.push(`${f.name}: ${problem}`)
    }
    return out
  }

  /** The record this modal already created, if a previous attempt failed
   *  partway through writing cells. Retrying REUSES it (SEN-1780): the
   *  natural response to the error was to press Create again, which made a
   *  second record every time. Reusing rather than deleting also keeps the
   *  half-written record's cells, so a retry only has to redo what failed —
   *  and it matches the create-more path, where the modal already stays
   *  mounted across writes. Cleared on success, exactly where `createMore`
   *  resets the rest of the form. */
  useEffect(() => {
    if (!metadataReady) return
    const seed = JSON.parse(seedKey) as Record<string, unknown>
    if (Object.keys(seed).length === 0) return
    setValues((p) => ({ ...seed, ...p }))
    // editors hold their own draft: remount the seeded ones so they show the default
    setFilledRev((r) => ({ ...r, ...Object.fromEntries(Object.keys(seed).map((id) => [id, (r[id] ?? 0) + 1])) }))
  }, [metadataReady, seedKey, fieldsKey])

  const createdRef = useRef<string | null>(null)
  /** Attributes this modal has SUCCESSFULLY written on `createdRef`'s record.
   *  A retry must clear the ones the user has since emptied (C4-5): the
   *  submit loop sends only non-empty fields, so a value written on attempt
   *  one and deleted before attempt two survived on the record — the user
   *  removed it, pressed Create, and it was still there. */
  const writtenRef = useRef<Set<string>>(new Set())

  async function submit() {
    // The guard lives in the HANDLER as well as on the button, because ⌘↵
    // calls submit() directly and would sail past a disabled control.
    if (!metadataReady) return
    const missing = missingRequired()
    if (missing.length > 0) {
      // Name the fields — a refusal a person can act on, not "invalid form".
      setError(missing.length === 1
        ? `${missing[0]} is required.`
        : `These fields are required: ${missing.join(', ')}.`)
      return
    }
    const badComposites = invalidComposites()
    if (badComposites.length > 0) { setError(badComposites.join(' ')); return }

    setSaving(true); setError(null)
    let record_id = createdRef.current
    try {
      if (!record_id) {
        record_id = (await monadClient.createRecord(slug, typeId)).record_id
        createdRef.current = record_id
      }
      for (const f of fields) {
        const v = values[f.attribute_id]
        const isEmpty = v === undefined || v === null || v === ''
        // Empty AND previously written ⇒ the user cleared it on this record:
        // send the clear. Empty and never written ⇒ nothing to say.
        // A pre-filled default the person cleared is cleared on the record too
        // (the server applied it on create).
        if (isEmpty && !writtenRef.current.has(f.attribute_id) && !(f.attribute_id in defaults)) continue
        // A default left as it was is already on the record (the server applied it on
        // create): writing it again would only add a "changed X to X" to its history.
        if (!isEmpty && f.attribute_id in defaults && !writtenRef.current.has(f.attribute_id)
          && JSON.stringify(v) === JSON.stringify(defaults[f.attribute_id])) continue
        {
          try {
            // `null` is the CLEAR seam (`client.ts:337` → `{clear:true}`).
            // `''` is a VALUE, and the number and entity-reference codecs
            // refuse it (C5-4) — so clearing a non-text field used to fail
            // rather than clear, and the retry never completed.
            await monadClient.setValue(slug, typeId, f.attribute_id, record_id, isEmpty ? null : v)
            if (isEmpty) writtenRef.current.delete(f.attribute_id)
            else writtenRef.current.add(f.attribute_id)
          } catch (e) {
            // NAME the field. The wire refusal is deliberately static
            // (SEN-1071) — "The supplied value could not be encoded for its
            // attribute's type." — so the only place that knows WHICH field
            // was refused is here, where the call was made.
            throw new Error(`${f.name}: ${(e as Error).message}`)
          }
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['mrl-kennings', slug, typeId] })
      // the set changed: every collection's pages, count and summary (and the rail count) re-read
      refreshAfterSetChange(queryClient)
      createdRef.current = null
      writtenRef.current = new Set()
      onCreated(record_id)
      // On success WITHOUT createMore, onClose() unmounts this component — calling
      // setSaving(false) after that would trigger a "setState on unmounted" warning.
      // Guard: only setSaving(false) when we stay mounted (createMore path).
      if (createMore) { setValues({}); setFilledRev({}); setFieldsKey((k) => k + 1); setSaving(false) } else onClose()
    } catch (e) {
      // The modal STAYS OPEN on a partial failure: the record exists with the
      // cells that did land, and closing here would hide that from the user.
      setError((e as Error).message)
      setSaving(false) // error path always stays mounted — finally not used to avoid the unmount edge case above
    }
  }

  // Register this modal's depth in the module-level stack so the keydown
  // handler can determine whether it is the topmost modal.
  const depthRef = useRef(depth)
  useEffect(() => {
    const d = depthRef.current
    _modalDepthStack.push(d)
    return () => {
      const idx = _modalDepthStack.lastIndexOf(d)
      if (idx !== -1) _modalDepthStack.splice(idx, 1)
    }
  }, [])

  // ⌘↵ / Esc act ONLY when this modal is the topmost in the stack.
  // Deps narrowed intentionally: the listener is re-installed whenever
  // values/createMore/fields change so it always closes over their latest
  // values without needing refs — this is the cost of avoiding stale closures.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally narrowed; listener re-installs per render of values/createMore/fields
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isTop = _modalDepthStack.length === 0 || Math.max(..._modalDepthStack) === depthRef.current
      if (!isTop) return
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void submit() }
      if (e.key === 'Escape') { e.preventDefault(); onClose() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [values, createMore, fields])

  // The FORM owns focus (editors must not autofocus in form context — the
  // last autofocuser would win the steal): focus the first field's control
  // once fields render, and again after a Create-more reset.
  const bodyRef = useRef<HTMLDivElement | null>(null)
  const firstFieldId = fields[0]?.attribute_id
  useEffect(() => {
    if (!firstFieldId) return
    const first = bodyRef.current?.querySelector<HTMLElement>(
      `[data-testid="create-field-${firstFieldId}"] input, [data-testid="create-field-${firstFieldId}"] button`,
    )
    first?.focus()
  }, [firstFieldId, fieldsKey])

  const title = (type as { singular_noun?: string; name?: string } | undefined)
  return createPortal(
    <div data-testid={`create-modal${depth ? `-${depth}` : ''}`} className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 pt-16" style={{ zIndex: 60 + depth }}>
      <div className="flex max-h-[80vh] w-[40rem] flex-col rounded-float border border-border bg-background shadow-float">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div data-testid="create-modal-title" className="text-sm font-semibold">
            Create {title?.singular_noun ?? title?.name ?? ''}
          </div>
          <Button variant="bare" data-testid="create-modal-close" onClick={onClose}>✕</Button>
        </div>
        <div ref={bodyRef} className="flex-1 overflow-y-auto px-4 py-3">
          <PlacePickContext.Provider value={fillFromPlace}>
          {fields.map((f) => {
            const Editor = resolveWidget(f.type)!.write!
            return (
              <div key={`${fieldsKey}-${f.attribute_id}-${filledRev[f.attribute_id] ?? 0}`} data-testid={`create-field-${f.attribute_id}`} className="mb-3 block text-sm">
                <span className="mb-1 block text-xs text-muted-foreground">
                  {f.name}
                  {f.is_required && <span aria-label="required" className="ml-0.5 text-muted-foreground">*</span>}
                  {typeof f.default_value?.system === 'string' && SYSTEM_DEFAULT_TEXT[f.default_value.system] && (
                    <span data-testid={`create-field-default-${f.attribute_id}`} className="ml-2 text-muted-foreground/70">
                      {SYSTEM_DEFAULT_TEXT[f.default_value.system]} if left empty
                    </span>
                  )}
                </span>
                <Editor
                  value={values[f.attribute_id] ?? null}
                  config={f.config ?? undefined}
                  context="form"
                  slug={slug}
                  typeId={typeId}
                  attributeId={f.attribute_id}
                  onCommit={(next) => setValues((p) => ({ ...p, [f.attribute_id]: next }))}
                  onCancel={() => {}}
                />
              </div>
            )
          })}
          </PlacePickContext.Provider>
          {/* Three DISTINCT states, told apart honestly. Presenting a pending
              form as an empty one is what made the required check bypassable:
              it looked finished when it was not. */}
          {attrsPending && (
            <div data-testid="create-fields-loading" className="py-2 text-sm text-muted-foreground">Loading fields…</div>
          )}
          {attrsError && (
            <div data-testid="create-fields-error" className="py-2 text-sm text-destructive">
              Could not load this type&apos;s fields: {(attrsErrorObj as Error | null)?.message ?? 'unknown error'}
              <Button variant="bare" data-testid="create-fields-retry" className="ml-2 underline" onClick={() => void refetchAttrs()}>Retry</Button>
            </div>
          )}
          {metadataReady && fields.length === 0 && <div className="py-2 text-sm text-muted-foreground">No writable fields on this type.</div>}
          {error && <div data-testid="create-error" className="py-1 text-sm text-destructive">{error}</div>}
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-border px-4 py-3">
          <label data-testid="create-more-toggle" className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground" onClick={() => setCreateMore((v) => !v)}>
            {/* The one checkbox visual (NomOS grammar) — not a raw input */}
            <Checkbox checked={createMore} onToggle={() => setCreateMore((v) => !v)} />
            Create more
          </label>
          <Button variant="primary" data-testid="create-submit" disabled={saving || !metadataReady} onClick={() => void submit()}
          >
            {saving ? 'Creating…' : 'Create record'} <span className="ml-1 text-xs opacity-70">⌘↵</span>
          </Button>
        </div>
      </div>
    </div>,
    container ?? document.body,
  )
}
